/**
 * Sign in: the approved customer OAuth surface, with the contributor copy
 * preserved when an invitation or workspace destination identifies that flow.
 *
 * What this page does and deliberately does not do:
 *
 *   * The buttons are driven by `/auth/providers`, so a provider without
 *     credentials in this environment is never offered.
 *   * The `next` and `invitation` parameters are read here, and only here
 *     (`invite` is accepted as a legacy alias — early links used it).
 *     `next` must be a same-origin path — anything absolute, protocol-
 *     relative, or backslashed is discarded, because it would be an open
 *     redirect wearing a query parameter. It is kept in `sessionStorage`
 *     across the provider round trip, since the server's callback lands on
 *     a fixed path and reads no redirect parameter by design.
 *   * The failure states are the three categories the server actually
 *     redirects with (`unavailable`, `retry`, `failed`) plus the pending-
 *     admission page. The server deliberately collapses its closed set of
 *     auth-error codes into those categories — unknown, expired, revoked and
 *     redeemed invitations are one indistinguishable answer — and this page
 *     renders exactly what it is told, never guessing at something finer.
 *   * An authenticated merchant resumes at the Business Profile; contributors
 *     follow their preserved destination or the workspace dashboard.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { useLanguage } from '../../i18n/language.js';
import { fetchProviders } from '../api.js';
import { browserNavigation } from '../navigation.js';
import { AppleMark, GoogleMark, useWorkspaceCopy } from '../components.js';
import { useSession } from '../session.js';
import { InlineAlert, Spinner } from '../ui.js';
import { useAsync } from './common.js';

const CUSTOMER_AUTH_COPY = {
  en: {
    title: 'Sign in to Mouja',
    sub: 'Sign in to continue to your Business Profile and Production Lines.',
    loading: 'Loading sign-in options…',
    redirecting: (provider: string) => `Redirecting to ${provider}…`,
    redirectingHint:
      'Finish signing in with your provider. This page continues on its own.',
    unavailable: 'Sign-in is temporarily unavailable.',
    failed: 'We couldn’t complete sign-in. Please try again.',
    offline: 'You’re offline. Reconnect to continue.',
    tryAgain: 'Try again',
  },
  ar: {
    title: 'سجّل الدخول إلى موجة',
    sub: 'سجّل الدخول للمتابعة إلى ملف نشاطك التجاري وخطوط الإنتاج.',
    loading: 'جارٍ تحميل خيارات تسجيل الدخول…',
    redirecting: (provider: string) => `جارٍ تحويلك إلى ${provider}…`,
    redirectingHint: 'أكمل تسجيل الدخول عبر المزوّد، وستتابع هذه الصفحة تلقائياً.',
    unavailable: 'تسجيل الدخول غير متاح مؤقتاً.',
    failed: 'تعذر إكمال تسجيل الدخول. حاول مرة أخرى.',
    offline: 'أنت غير متصل. أعد الاتصال للمتابعة.',
    tryAgain: 'حاول مرة أخرى',
  },
} as const;

/** Where the preserved destination lives across the provider round trip. */
export const NEXT_STORAGE_KEY = 'mouja:next';
/** Set when sign-in starts, so the landing knows a fresh session must route. */
export const PENDING_SIGNIN_KEY = 'mouja:signin-pending';

/** Accept only a same-origin absolute path. Anything else is discarded. */
export function safeNextPath(candidate: string | null): string | null {
  if (candidate === null || candidate.length === 0 || candidate.length > 500)
    return null;
  if (!candidate.startsWith('/')) return null;
  if (candidate.startsWith('//') || candidate.includes('\\')) return null;
  return candidate;
}

export function SignInPage({
  variant = 'form',
}: {
  /** `pending` renders the owner-confirmation state the callback lands on. */
  readonly variant?: 'form' | 'pending';
}): ReactNode {
  const copy = useWorkspaceCopy();
  const { language } = useLanguage();
  const { session } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [redirectingProvider, setRedirectingProvider] = useState<string | null>(null);
  const [online, setOnline] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );

  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const next = safeNextPath(query.get('next'));
  // `invitation` is the canonical parameter — it is what the server's
  // invitation-link builder (`invitationUrlFor`) has always emitted. `invite`
  // is kept as a fallback for links minted while the page briefly read only
  // that spelling. The token is never logged, rendered, or stored: it goes
  // into the OAuth start URL and nowhere else.
  const invite = query.get('invitation') ?? query.get('invite');
  const errorCategory = query.get('error');

  const providers = useAsync(useCallback(() => fetchProviders(), []));
  const customerDestination =
    invite === null &&
    (next === null ||
      next === '/business-profile' ||
      next.startsWith('/production-lines'));
  const customerCopy = CUSTOMER_AUTH_COPY[language];

  useEffect(() => {
    const connected = () => {
      setOnline(true);
    };
    const disconnected = () => {
      setOnline(false);
    };
    window.addEventListener('online', connected);
    window.addEventListener('offline', disconnected);
    return () => {
      window.removeEventListener('online', connected);
      window.removeEventListener('offline', disconnected);
    };
  }, []);

  // An authenticated visitor opening /signin goes straight to the dashboard.
  useEffect(() => {
    if (session.status === 'authenticated' && redirectingProvider === null) {
      void navigate(
        session.account.role === 'MERCHANT'
          ? '/business-profile'
          : (next ?? '/dashboard'),
        { replace: true },
      );
    }
  }, [session, navigate, next, redirectingProvider]);

  const begin = useCallback(
    (provider: string) => {
      if (!online) return;
      setRedirectingProvider(provider);
      try {
        if (next !== null) sessionStorage.setItem(NEXT_STORAGE_KEY, next);
        else sessionStorage.removeItem(NEXT_STORAGE_KEY);
        sessionStorage.setItem(PENDING_SIGNIN_KEY, '1');
      } catch {
        // Storage can be unavailable in private modes; sign-in still works,
        // only the return-to-page nicety is lost.
      }
      const invitation =
        invite !== null && invite.length > 0
          ? `?invitation=${encodeURIComponent(invite)}`
          : '';
      browserNavigation.assign(`/auth/${provider}/start${invitation}`);
    },
    [next, invite, online],
  );

  const title = customerDestination ? customerCopy.title : copy.auth.title;
  const subtitle = customerDestination ? customerCopy.sub : copy.auth.sub;

  const retry = () => {
    if (errorCategory !== null) void navigate('/signin', { replace: true });
    providers.reload();
  };

  const providerButtons =
    providers.state.status === 'ready' ? (
      <div className="mj-auth-providers">
        {providers.state.value.providers.includes('google') ? (
          <button
            type="button"
            className="mj-auth-provider"
            disabled={!online}
            onClick={() => {
              begin('google');
            }}
          >
            <GoogleMark />
            {copy.auth.continueWithGoogle}
          </button>
        ) : null}
        {providers.state.value.providers.includes('apple') ? (
          <button
            type="button"
            className="mj-auth-provider is-apple"
            disabled={!online}
            onClick={() => {
              begin('apple');
            }}
          >
            <AppleMark />
            {copy.auth.continueWithApple}
          </button>
        ) : null}
      </div>
    ) : null;

  const surface = (
    <section className="mj-auth-card" aria-labelledby="mj-auth-title">
      <h1 id="mj-auth-title">
        {variant === 'pending' ? copy.auth.pendingTitle : title}
      </h1>

      {variant === 'pending' ? (
        <>
          <p>{copy.auth.pendingBody}</p>
          <Link className="mj-auth-link" to="/signin">
            {copy.auth.backToSignIn}
          </Link>
        </>
      ) : (
        <>
          <p>{subtitle}</p>

          {errorCategory !== null ? (
            <AuthRecovery
              message={
                customerDestination
                  ? errorCategory === 'unavailable'
                    ? customerCopy.unavailable
                    : customerCopy.failed
                  : errorCategory === 'unavailable'
                    ? copy.auth.errorUnavailable
                    : errorCategory === 'retry'
                      ? copy.auth.errorRetry
                      : copy.auth.errorFailed
              }
              retryLabel={
                customerDestination ? customerCopy.tryAgain : copy.actions.retry
              }
              onRetry={retry}
            />
          ) : null}

          {invite !== null && invite.length > 0 ? (
            <InlineAlert tone="info">{copy.auth.invitationNoted}</InlineAlert>
          ) : null}

          {errorCategory !== null ? null : redirectingProvider !== null ? (
            <div className="mj-auth-progress" role="status" aria-live="polite">
              <Spinner
                label={
                  customerDestination
                    ? customerCopy.redirecting(providerName(redirectingProvider))
                    : copy.auth.redirecting
                }
              />
              {customerDestination ? <p>{customerCopy.redirectingHint}</p> : null}
            </div>
          ) : session.status === 'loading' ? (
            <Spinner
              label={customerDestination ? customerCopy.loading : copy.states.loading}
            />
          ) : !online ? (
            <>
              <div className="mj-auth-message is-warning" role="status">
                <span aria-hidden="true">!</span>
                <p>
                  {customerDestination ? customerCopy.offline : copy.states.offlineBody}
                </p>
              </div>
              {providerButtons}
            </>
          ) : providers.state.status === 'loading' ? (
            <Spinner
              label={customerDestination ? customerCopy.loading : copy.states.loading}
            />
          ) : providers.state.status === 'failed' ? (
            <AuthRecovery
              message={
                customerDestination ? customerCopy.unavailable : copy.auth.errorRetry
              }
              retryLabel={
                customerDestination ? customerCopy.tryAgain : copy.actions.retry
              }
              onRetry={retry}
            />
          ) : providers.state.value.providers.length === 0 ? (
            <AuthRecovery
              message={
                customerDestination ? customerCopy.unavailable : copy.auth.noProviders
              }
              retryLabel={
                customerDestination ? customerCopy.tryAgain : copy.actions.retry
              }
              onRetry={retry}
            />
          ) : (
            providerButtons
          )}

          {!customerDestination ? (
            <p className="mj-auth-note">{copy.auth.limited}</p>
          ) : null}
        </>
      )}
    </section>
  );

  return (
    <div className="mj-auth-page" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <header className="mj-auth-brand">
        <img
          src={
            language === 'ar'
              ? '/mouja-arabic-pixel-media-logo.svg'
              : '/mouja-pixel-media-logo.svg'
          }
          alt={language === 'ar' ? 'موجة' : 'Mouja'}
        />
      </header>
      <main className="mj-auth-main">{surface}</main>
    </div>
  );
}

function providerName(provider: string): string {
  if (provider === 'google') return 'Google';
  if (provider === 'apple') return 'Apple';
  return provider;
}

function AuthRecovery({
  message,
  retryLabel,
  onRetry,
}: {
  readonly message: string;
  readonly retryLabel: string;
  readonly onRetry: () => void;
}): ReactNode {
  return (
    <div className="mj-auth-recovery">
      <div className="mj-auth-message is-error" role="alert">
        <span aria-hidden="true">!</span>
        <p>{message}</p>
      </div>
      <button type="button" className="mj-auth-retry" onClick={onRetry}>
        {retryLabel}
      </button>
    </div>
  );
}
